import { useCallback, useEffect, useMemo } from 'react'

import { useBrowserStoredState } from '@syconium/little-miss-figgy'

import { useLocalization } from '../../../app/_providers/LocalizationProvider.client'
import { useSession } from '../../../app/_providers/SessionProvider.client'
import { useTranslation } from '../../../app/_providers/TranslationProvider.client'
import { useFixturesContext } from '../../../brunswick/containers/fixtures'
import { NavAccordion, NavTab } from '../../../types/graphql/site-fixtures'
import { useAccountMenuItems } from '../useAccountMenuItems'

export function useViewModel({
  closeModal,
  renderAllNavigationTabs,
}: {
  closeModal: () => void
  renderAllNavigationTabs: boolean
}) {
  const {
    magnolia: { navigation: strings },
  } = useTranslation()
  const { navigation } = useFixturesContext()
  const { mobileTabs, helpLinkText, helpLinkHref } = navigation || {}
  const { region } = useLocalization()
  const { accountMenuItems } = useAccountMenuItems()
  const { setStylePreference } = useSession()

  // NB: NavAccordion is the NavSection, not NavAccordionSection.
  const makeAccountNavSection = useCallback(
    (tabId: string): NavAccordion => ({
      __typename: 'NavAccordion',
      id: `account_${tabId}`,
      variant: 'default',
      sections: [
        {
          body: {
            __typename: 'NavListSection',
            id: `account_${tabId}`,
            items: accountMenuItems,
            variant: 'default',
          },
          header: {
            __typename: 'NavListItem',
            text: 'Account',
            id: '',
            badge: null,
            badgeColor: null,
            badgeTextColor: null,
            href: null,
            icon: null,
            isBold: null,
            isHeading: null,
            textColor: null,
            htmlId: null,
            htmlClass: null,
            isBtn: null,
            alternateModelVariant: null,
          },
          id: `account_${tabId}`,
          alternateModelVariant: null,
        },
      ],
    }),
    [accountMenuItems]
  )

  const tabs = useMemo(() => {
    return (
      mobileTabs?.map(o => ({ ...o, sections: [...o.sections, makeAccountNavSection(o.id)] })) || []
    )
  }, [makeAccountNavSection, mobileTabs])

  const { state, setState } = useBrowserStoredState<{ activeTabId: string | undefined }>({
    initialState: { activeTabId: tabs[0]?.id },
    storageKey: 'NavModalContent',
    ssr: true,
  })

  const activeTabId = tabs.some((o): boolean => o.id === state.activeTabId)
    ? state.activeTabId
    : tabs[0]?.id
  const activeTab = tabs.find(o => o.id === activeTabId)
  const setActiveTab = useCallback((tab: NavTab) => setState({ activeTabId: tab.id }), [setState])

  // set navGenderCookie on initial render (NavModal open) and activeTab change (Tab click)
  // - only set if different from navGenderCookie to prevent infinite loop of state changes
  useEffect(() => {
    /**
     * Note: do not set navGenderCookie when renderAllNavigationTabs === true
     * since it means that a hidden navigation will be rendered for SEO purposes
     * and not the navigation that users would actually use
     */
    // TODO: Fix this auto-generated suppression.
    // eslint-disable-next-line react-you-might-not-need-an-effect/no-event-handler
    if (!renderAllNavigationTabs) {
      setStylePreference(getTabGender(activeTab))
    }
    // TODO: Fix this auto-generated suppression.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab])

  return {
    activeTab,
    closeButtonLabel: strings.closeButtonLabel,
    closeModal,
    helpLinkHref: helpLinkHref ?? undefined,
    helpLinkText: helpLinkText ?? undefined,
    regionId: region.id,
    regionName: region.name,
    openFirstNavAccordionSection: false,
    renderAllNavigationTabs,
    setActiveTab,
    tabs,
  }
}

/**
 * Warning: dependency on English.
 * TODO: add Contentful NavTab.gender and `setNavGenderCookie(tab.gender)`
 */
export function getTabGender(tab?: { text: string | undefined | null }) {
  if (tab?.text?.toLowerCase().includes('women')) return 'women'
  if (tab?.text?.toLowerCase().includes('men')) return 'men'
  return undefined
}
